import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';

import JxForm from '../JxForms/JxForm';
import JxTitleBar from '../JxTitleBar/JxTitleBar';
import JxIconButton from '../JxButtons/JxIconButton';
import JxButton from '../JxButtons/JxButton';
import JxSelect from '../JxSelect/JxSelect';
import JxCheckbox from '../JxToggles/JxCheckbox';
import JxLabel from '../JxLabels/JxLabel';
import { TIMEZONE_OPTIONS } from './clockTimezones';

import '../JxTheme.css';
import './JxClockOptionsDialog.css';

const DEFAULT_POS = () => ({ top: Math.max(20, (window.innerHeight - 200) / 2), left: Math.max(20, (window.innerWidth - 280) / 2) });

const FIELD_TIMEZONE = 'timeZone';
const FIELD_THIS_TAB = 'thisTabOnly';

const DEFAULT_INPUTS = {
    [FIELD_TIMEZONE]: 'Europe/Amsterdam',
    [FIELD_THIS_TAB]: true,
};

/**
 * JxClockOptionsDialog — option builder for JxClockTestForm.
 * Shown by JxWindowManager before opening a clock window.
 * Calls onExit(owner, data) on confirm — owner/data null on cancel.
 * bringToTop on mount/open handled by JxForm base.
 *
 * @param {boolean}  props.open
 * @param {string}   props.currentTabId - active tab id, used when thisTabOnly is checked
 * @param {function} props.onExit - (owner, data) => void
 */
export default class JxClockOptionsDialog extends JxForm {
    constructor(props) {
        super(props);
        this.name = 'JxClockOptionsDialog';
        this.schema = {};
        this.state = { ...this.state, inputs: { ...DEFAULT_INPUTS } };
    }

    getInitialPos = () => DEFAULT_POS();

    handleChange = (field, value) => {
        this.setState((s) => ({ inputs: { ...s.inputs, [field]: value } }));
    };

    handleConfirm = () => {
        const { inputs } = this.state;
        const owner = inputs[FIELD_THIS_TAB] ? (this.props.currentTabId ?? 'global') : 'global';
        const data  = { timeZone: inputs[FIELD_TIMEZONE] };
        this.props.onExit(owner, data);
    };

    handleClose = () => {
        this.props.onExit(null, null);
    };

    render() {
        if (!this.props.open) return null;

        const { inputs } = this.state;

        return createPortal((
            <div {...this.panelProps} style={this.panelStyle} className={`jx-clock-options-dialog ${this.panelActiveClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Clock Options"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={<JxIconButton icon={<CloseIcon sx={{ fontSize: 16 }} />} title="Close" onClick={this.handleClose} />}
                />

                <div className="jx-clock-options-body">
                    <div className="jx-clock-options-row">
                        <JxLabel text="Timezone" />
                        <JxSelect
                            id={`${this.name}_timezone`}
                            value={inputs[FIELD_TIMEZONE]}
                            onChange={(v) => this.handleChange(FIELD_TIMEZONE, v)}
                            options={TIMEZONE_OPTIONS}
                            defaultLabel="Select timezone…"
                        />
                    </div>
                    <div className="jx-clock-options-row jx-clock-options-row--inline">
                        <JxLabel text="This tab only" />
                        <JxCheckbox
                            id={`${this.name}_thistab`}
                            checked={inputs[FIELD_THIS_TAB]}
                            onChange={(v) => this.handleChange(FIELD_THIS_TAB, v)}
                        />
                    </div>
                </div>

                <div className="jx-clock-options-footer">
                    <JxButton label="Cancel" variant="secondary" onClick={this.handleClose} />
                    <JxButton label="Open" variant="primary" onClick={this.handleConfirm} />
                </div>
            </div>
        ), document.body);
    }
}
